import { Grid, Card, CardContent, Typography, IconButton, TextField, InputAdornment, FormControlLabel, Checkbox, Collapse, Radio, Button, Box } from "@mui/material";
import { t } from "i18next";
import React, { useRef, useEffect, useState } from "react";
import { List, OnChangeMeta, arrayMove } from "react-movable";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { VariationTypeSelectProps, VariationDataType, VariationPayloadRequest } from "../../../interfaces/productVariationInterface";
import SimpleReactValidator from "simple-react-validator";

const VariationTypeSelect = (props: VariationTypeSelectProps) => {
    const {variations, setVariations, setIsSaveEnabled, setProductVariationPayload, productVariationPayload, setCheckedVariationType, checkedVariationType, previousProducts, setVariationToDelete, setModalOpen , deletedVariationTypes, isEditMode,editModeInitialRender,setEditModeInitialRender ,setFieldErrors,setCompletedDataSet,completedDataSet,mainValidator } = props;
    // Track if initial load has been completed
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [editIndex, setEditIndex] = useState<any>(null);
    const [addinputbox, setaddinputbox] = useState<number[]>([]);
    const [editName, setEditName] = useState("");
    const [showInput, setShowInput] = useState<boolean>(false);
    const [newVariationName, setNewVariationName] = useState("");
    const skipPayloadEffectRef = useRef(false);

    const [, forceUpdate] = useState(0);
    const validator = useRef(
        new SimpleReactValidator({
            autoForceUpdate: { forceUpdate },
            className: "text-danger",
            messages: {
                required: t("required.this_field_cannot_be_empty"),
            },
            validators: {
                maxChar15Validation: {
                    message: t("product_variation_list.max_char_input_field_validation", { size: 15 }).toString(),
                    rule: (value: string, params: number) => {
                        return value.length <= params;
                    },
                },
            },
        })
    );

    const handleSaveEdit = (index: number) => {
        if (validator.current.allValid()) {
            const updatedVariations = [...variations];
            updatedVariations[index].name = editName;
            setVariations(updatedVariations);
            setEditIndex(null);
            setEditName("");
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };
    // Load previous products data only once on initial render
    useEffect(() => {
        // Skip if already loaded or if no previous products or variation types
        if (initialLoadComplete || !previousProducts[0]?.variationTypes?.length) return;
        
        // Use a ref to prevent this effect from updating the payload
        // This will allow the parent component to control when the payload is updated
        if (skipPayloadEffectRef.current) {
            skipPayloadEffectRef.current = false;
            return;
        }
        
        const previousVariations = previousProducts[0]?.variationTypes;
        const newCheckedTypes = [...checkedVariationType]; // Create a copy to collect all types
        
        // First, collect all the variations we need to update or add
        const updatedVariations = [...variations];
        const newVariationsToAdd: VariationDataType[] = [];

        if(editModeInitialRender){
            return;
        }
        
        // Process each previous variation
        previousVariations.forEach((prevVariation) => {
            // Skip if this variation type has been explicitly deleted by the user
            if (deletedVariationTypes.includes(prevVariation.variationType)) {
                return;
            }
            
            // Add to checked types if not already included
            if (!newCheckedTypes.includes(prevVariation.variationType)) {
                newCheckedTypes.push(prevVariation.variationType);
            }
            
            // Determine display type
            const displayType = prevVariation.displayType === "3" ? "Radio Button" : "List Drop-down";
            
            // Check if this variation type already exists in our variations array
            const existingIndex = variations.findIndex(v => v.name === prevVariation.variationType);
            
            if (existingIndex >= 0) {
                // Update existing variation
                updatedVariations[existingIndex] = {
                    ...updatedVariations[existingIndex],
                    checked: true,
                    type: displayType
                };
            } else {
                // Add new variation
                newVariationsToAdd.push({
                    name: prevVariation.variationType,
                    checked: true,
                    type: displayType,
                    isCustom: true,
                    isEditing: false
                });
            }
        });
        
        // Separate checked and unchecked variations
        const checkedVariations = [...updatedVariations, ...newVariationsToAdd].filter(v => v.checked);
        const uncheckedVariations = [...updatedVariations, ...newVariationsToAdd].filter(v => !v.checked);
        
        // Combine with unchecked at the bottom
        const combinedVariations = [...checkedVariations, ...uncheckedVariations];

        // Update variations state once with all changes
        setVariations(combinedVariations);
        
        // Update checked variation types once
        setCheckedVariationType(newCheckedTypes);
        
        // Update save button state if we have variations
        if (updatedVariations.some(v => v.checked) || newVariationsToAdd.length > 0) {
            setIsSaveEnabled(true);
        }
        
        // Mark initial load as complete so this effect doesn't run again
        setInitialLoadComplete(true);
        setEditModeInitialRender(true);
    }, [initialLoadComplete, previousProducts, variations, checkedVariationType, deletedVariationTypes]);


    const manageVariationTypeOrder = () => {
        // Skip if no previous products or not in edit mode
        if (!isEditMode || !previousProducts?.[0]?.variationTypes?.length) return;
        
        // Set the flag to prevent the other useEffect from updating the payload
        skipPayloadEffectRef.current = true;
        
        const previousVariations = previousProducts?.[0]?.variationTypes;        
        const firstVariationIndex = variations?.findIndex((item) => item.name === previousVariations?.[0]?.variationType);        
        const secondVariationIndex = variations?.findIndex((item) => item.name === previousVariations?.[1]?.variationType);

        const updatedVariations = [...variations];

        if(firstVariationIndex === -1 && secondVariationIndex != -1){
        // custom variation type is at first index
            updatedVariations.unshift({
                name : previousVariations?.[0]?.variationType,
                checked : true,
                isCustom : true,
                isEditing : false,
                type : previousVariations?.[0]?.displayType === "3" ? "Radio Button" : "List Drop-down"
            });

            // fixed variation type at second index
            const secondElement = updatedVariations.findIndex((item) => item.name === previousVariations?.[1]?.variationType);

            if(secondElement != -1 && previousVariations && previousVariations[1] && previousVariations[1].variationType){
                updatedVariations[secondElement] =  {
                    name : previousVariations?.[1]?.variationType,
                    checked : true,
                    isCustom : false,
                    isEditing : false,
                    type : previousVariations?.[1]?.displayType === "3" ? "Radio Button" : "List Drop-down"
                };
            }
        }
        else if(secondVariationIndex === -1 && firstVariationIndex != -1){
        // fixed variation type at first index
            const firstElementIndex = updatedVariations.findIndex((item) => item.name === previousVariations?.[0]?.variationType);
            updatedVariations[firstElementIndex] = {
                ...variations[firstElementIndex],
                name : previousVariations?.[0]?.variationType,
                checked : true,
                type : previousVariations?.[0]?.displayType === "3" ? "Radio Button" : "List Drop-down"
            };
            // custom variation type at second index
            if(previousVariations && previousVariations[1] && previousVariations[1]?.variationType ){
                updatedVariations.push({
                    name : previousVariations?.[1]?.variationType,
                    checked : true,
                    isCustom : true,
                    isEditing : false,
                    type : previousVariations?.[1]?.displayType === "3" ? "Radio Button" : "List Drop-down"
                });
            }
        }
        // Both variations are custom
        else if(firstVariationIndex === -1 && secondVariationIndex === -1){
        // custom variation type at first index
            updatedVariations.push({
                name : previousVariations?.[0]?.variationType,
                checked : true,
                isCustom : true,
                isEditing : false,
                type : previousVariations?.[0]?.displayType === "3" ? "Radio Button" : "List Drop-down"
            });

            if(previousVariations && previousVariations[1] && previousVariations[1]?.variationType){
                // custom variation type at second index
                updatedVariations.push({
                    name : previousVariations?.[1]?.variationType,
                    checked : true,
                    isCustom : true,
                    isEditing : false,
                    type : previousVariations?.[1]?.displayType === "3" ? "Radio Button" : "List Drop-down"
                });
            }
        }
        // Both Variation are fixed 
        else if(firstVariationIndex != -1 && secondVariationIndex != -1){
        // fixed variation type at first index                
            updatedVariations[0] = {
                ...variations[0],
                name : previousVariations?.[0]?.variationType,
                checked : true,
                type : previousVariations?.[0]?.displayType === "3" ? "Radio Button" : "List Drop-down"
            };
            // fixed variation type at second index
            if(previousVariations && previousVariations[1] && previousVariations[1].variationType){
                updatedVariations[1] = {
                    ...variations[1],
                    name : previousVariations?.[1]?.variationType,
                    checked : true,
                    type : previousVariations?.[1]?.displayType === "3" ? "Radio Button" : "List Drop-down"
                };
            }
        }
        
        // Separate checked and unchecked variations
        const checkedVariations = updatedVariations.filter(v => v.checked);
        const uncheckedVariations = updatedVariations.filter(v => !v.checked);
        
        // Combine with unchecked at the bottom
        const sortedVariations = [...checkedVariations, ...uncheckedVariations];
        
        // Only update if there are changes
        if (JSON.stringify(sortedVariations) !== JSON.stringify(variations)) {
            setVariations(sortedVariations);
        }
    };

    // Run once on initial render
    useEffect(() => {
        if (isEditMode && previousProducts?.length > 0  && !editModeInitialRender) {
            manageVariationTypeOrder();
        }
    }, [isEditMode, previousProducts?.length]);

    const handleTypeChange = (index: number, type: "Radio Button" | "List Drop-down") => {
        const newVariations = [...variations];
        newVariations[index].type = type;
        setVariations(newVariations);
    
        // Only update payload if this variation is checked/selected
        if (newVariations[index].checked) {
            const variationName = newVariations[index].name;
            const displayType = type === "Radio Button" ? "3" : "4"; // 3 for Radio Button, 4 for List Drop-down
        
            // Update completedDataSet with the new displayType
            setCompletedDataSet((prevCompletedData: any) => {
                return prevCompletedData.map((item: any) => {
                // Skip products marked for deletion
                    if (item.postingStatus === 9) return item;
                
                    // Find if this item has this variation type
                    const variationIndex = item.variations?.findIndex(
                        (v: any) => v.variationType && v.variationType.normalize("NFKC").toLowerCase() === variationName.normalize("NFKC").toLowerCase()
                    );
                
                    if (variationIndex >= 0) {
                    // Create a new variations array with the updated displayType
                        const updatedVariations = [...item.variations];
                        updatedVariations[variationIndex] = {
                            ...updatedVariations[variationIndex],
                            displayType
                        };
                    
                        return {
                            ...item,
                            variations: updatedVariations
                        };
                    }
                    return item;
                });
            });

            setProductVariationPayload((prev) => ({
                ...prev,
                productVariations : prev.productVariations.map((item)=> {
                    if (item.postingStatus === 9) return item;
                
                    // Find if this item has this variation type
                    const variationIndex = item.variations?.findIndex(
                        (v: any) => v.variationType && v.variationType.normalize("NFKC").toLowerCase() === variationName.normalize("NFKC").toLowerCase()
                    );

                    if (variationIndex >= 0) {
                    // Create a new variations array with the updated displayType
                        const updatedVariations = [...item.variations];
                        updatedVariations[variationIndex] = {
                            ...updatedVariations[variationIndex],
                            displayType : item.postingStatus === 9 ? item.variations[variationIndex].displayType : displayType
                        };
                    
                        return {
                            ...item,
                            variations: updatedVariations
                        };
                    }
                    return item;
                })
            }));
        }
    };

    const handleOpenModal = (index: number) => {
        validator.current.fields = {};
        forceUpdate(1);
        setEditName("");
        setEditIndex(null);
        setVariationToDelete(index);
        setModalOpen(true);
    };

    const closeCustomVariation = () => {
        setNewVariationName("");
        setShowInput(false);
    };

    const handleCustomVariation = () => {
        setShowInput(true);
    };
    /**
     * Adds a new custom variation to the list of variations.
     * This function first validates all form inputs using the `validator`. If the inputs
     * are valid and the new variation name is not empty, it creates a new variation object
     * with default properties and adds it to the `variations` state. The newly added variation
     * is marked as a custom variation and is unchecked by default.
     * @function
     * @returns {void}
     */
    const addCustomVariation = () => {
        if (validator.current.allValid()) {
            if (newVariationName.trim() === "") return;
            
            // Add the new variation - it will be automatically disabled if 2 variations are already checked
            // due to the disableHandler function
            setVariations([
                ...variations,
                { name: newVariationName, checked: false, type: "Radio Button", isCustom: true, isEditing: false },
            ]);
            setNewVariationName("");
            setShowInput(false);
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };

    
    /**
     * Handles the drag-and-drop event for reordering product variations. 
     * This function is triggered when a variation item is dragged and dropped
     * to a new position.
     * @param {Object} param - Drag event data.
     * @param {number} param.oldIndex - The original index of the dragged item.
     * @param {number} param.newIndex - The new index of the dropped item.
     */
    const onDragEnd = ({ oldIndex, newIndex } : OnChangeMeta) => {
        if (oldIndex === newIndex) return;

        // Set flag to skip the effect
        skipPayloadEffectRef.current = true;

        // Reorder the variations array
        const updatedVariations = arrayMove(variations, oldIndex, newIndex);
        setVariations(updatedVariations);

        // Get the variation names that were swapped
        const sourceVariation = variations[oldIndex];
        const destinationVariation = variations[newIndex];

        // Only proceed if both variations are checked
        if (sourceVariation.checked && destinationVariation.checked) {
        // Create a deep copy of the current payload
            // const updatedPayload: VariationPayloadRequest = {
            //     ...productVariationPayload,
            //     productVariations: productVariationPayload.productVariations.map(product => {
            //     // Create a new array for this product's variations
            //         const newVariations = [...product.variations];
                
            //         // Find indices of the variations to swap
            //         const sourceIndex = newVariations.findIndex(
            //             v => v.variationType === sourceVariation.name
            //         );
            //         const destIndex = newVariations.findIndex(
            //             v => v.variationType === destinationVariation.name
            //         );

            //         // Only swap if both exist
            //         if (sourceIndex >= 0 && destIndex >= 0) {
            //         // Perform the swap
            //             [newVariations[sourceIndex], newVariations[destIndex]] = 
            //             [newVariations[destIndex], newVariations[sourceIndex]];
            //         }

                    
            //         return {
            //             ...product,
            //             variations: newVariations
            //         };
            //     })
            // };
            
            // // Update the payload
            // setProductVariationPayload(updatedPayload);

            setCompletedDataSet((prev : any) => {
                return prev.map((product : any) => {
                    const orderedVariations = newCheckedTypes
                        .map(type => product.variations.find((v :any) => v.variationType === type))
                        .filter(Boolean)
                        .map((v, i) => ({ ...v, displayOrder: i + 1 }));
            
                    const otherVariations = product.variations
                        .filter((v : any) => !newCheckedTypes.includes(v.variationType));
            
                    return {
                        ...product,
                        variations: [...orderedVariations, ...otherVariations]
                    };
                });
            });
        }
            
        // Update checkedVaritionType array to match new order
        const newCheckedTypes = updatedVariations
            .filter(v => v.checked)
            .map(v => v.name);
        setCheckedVariationType(newCheckedTypes);
    };

    const handleEditVariation = (index: number) => {
        mainValidator.current.fields = {};
        mainValidator.current.errorMessages = {};
        setEditIndex(index);
        setEditName(variations[index].name);
        setShowInput(false);
        setNewVariationName("");
    };

    /**
     * Handles the checkbox toggle logic for product variations.
     * This function is triggered when a variation checkbox is clicked. It updates the variation's
     * `checked` state, manages the list of checked variations (`checkedVaritionType`), enables or disables
     * the save button based on selection state, and updates the product variation payload accordingly.
     * @param {number} index - The index of the variation in the `variations` array being toggled.
     */
    const handleCheckboxChange = (index: number,name:any) => {
        // Get the current checked state of the variation
        const currentChecked = variations[index].checked;
        
        // If trying to check a new variation but already have 2 checked, prevent the action
        if (!currentChecked && checkedVariationType.length >= 2) {
            return; // Don't allow checking more than 2 variations
        }
        
        // Create a copy of variations and toggle the checked state
        const newVariations = [...variations];
        newVariations[index].checked = !currentChecked;
        setVariations(newVariations);

        // Enable/disable save button based on whether any variation is checked
        const isAnyChecked = newVariations.some((variation) => variation.checked);
        setIsSaveEnabled(isAnyChecked);

        // Get the variation name and its new checked state
        const variationName = variations[index].name;
        const isChecked = newVariations[index].checked;
        
        // Update checkedVaritionType array
        if (isChecked) {
            // Add to checkedVaritionType if not already present
            if (!checkedVariationType.includes(variationName)) {
                // Get all checked variations in their current order in the UI
                const orderedCheckedVariations = newVariations
                    .filter(v => v.checked)
                    .map(v => v.name);
                
                // updating the checkVariationType after reordering
                setCheckedVariationType(orderedCheckedVariations);
            }
        } else {
            // Remove from checkedVaritionType
            setCheckedVariationType(checkedVariationType.filter(val => val !== variationName));
        }
        
        // Create a deep copy of the current payload to avoid reference issues
        const updatedPayload : VariationPayloadRequest = JSON.parse(JSON.stringify(productVariationPayload));
        
        // Get all checked variations in their original order from the variations state
        const checkedVariationsInOrder = newVariations
            .map((variation, idx) => ({ variation, originalIndex: idx }))
            .filter(item => item.variation.checked)
            .sort((a, b) => a.originalIndex - b.originalIndex);
        
        // Update the product variation payload
        updatedPayload.productVariations = updatedPayload.productVariations.map((product) => {
            // Ensure variations array exists
            if (!product.variations) {
                product.variations = [];
            }
            
            // If unchecking, remove the variation
            if (!isChecked) {
                return {
                    ...product,
                    variations: product.variations.filter((variation) => 
                        !variation || !variation.variationType || 
                        variation.variationType.normalize("NFKC").toLowerCase() !== variationName.normalize("NFKC").toLowerCase()
                    )
                };
            }

            if(product.postingStatus === 9){
                return {
                    ...product,
                    variations : product.variations
                };
            }
            
            
            // Create a new variations array for this product
            const newProductVariations:any = [];
            
            // Process each checked variation in order
            for (let i = 0; i < checkedVariationsInOrder.length; i++) {
                const item = checkedVariationsInOrder[i];
                const currentVariation = item.variation;
                const currentVariationName = currentVariation.name;
                const currentDisplayType = currentVariation.type === "Radio Button" ? "3" : "4";
                
                // Check if this variation already exists in the product
                const existingVariation = product.variations.find(
                    (v) => v && v.variationType && v.variationType.normalize("NFKC").toLowerCase() === currentVariationName.normalize("NFKC").toLowerCase()
                );
                
                if (existingVariation) {
                    // Variation already exists, update it with the correct display order
                    newProductVariations.push({
                        ...existingVariation,
                        displayType: currentDisplayType,
                        displayOrder: i + 1
                    });
                } else if (currentVariationName === variationName) {
                    // This is the newly checked variation, add it
                    newProductVariations.push({
                        variationType: currentVariationName,
                        variationValue: "",
                        displayType: currentDisplayType,
                        displayOrder: i + 1
                    });
                }
            }
            
            return {
                ...product,
                variations: newProductVariations
            };
        }); 
               
        setFieldErrors((prev: any) => {
            const newErrors = { ...prev };
            delete newErrors[`variationValue-${index}-${name}`];
            mainValidator.current.fields = {};
            mainValidator.current.errorMessages = {};
            return newErrors;
        });
        
        // Update the payload
        setProductVariationPayload(updatedPayload);
    };
    /**
    * Handles the editing of a variation name at a specified index.
    * - Prevents duplication of variation names.
    * - Updates the variation name in the `variations` state.
    * - Resets the editing state (`editIndex` and `editName`).
    * - If the old name exists in `checkedVaritionType`, replaces it with the new name.
    * @param {string} editName - The new name to assign to the variation.
    * @param {number} index - The index of the variation being edited.
    */
    const editFieldHandler = (editName: string, index: number) => {
        if (validator.current.allValid()) {
            const updatedVariations = [...variations];
            const oldName = updatedVariations[index].name; // Store the old name
            // Prevent duplicate name
            if (updatedVariations.find((v, i) => v.name === editName && i !== index)) {
                return;
            }
            // Update variation name
            updatedVariations[index] = {
                ...updatedVariations[index],
                name: editName,
            };
            setVariations(updatedVariations);
            setEditIndex(null);
            setEditName("");
            // Update checked variation type if it includes the old name
            if (checkedVariationType.includes(oldName)) {
                const updatedChecked = checkedVariationType.map((val) =>
                    val === oldName ? editName : val
                );
                setCheckedVariationType(updatedChecked);
            }
            // Update productVariationPayload: replace oldName with editName in all product variations
            setProductVariationPayload((prevPayload) => ({
                ...prevPayload,
                productVariations: prevPayload.productVariations.map((product) => ({
                    ...product,
                    variations: product.variations.map((variation) =>
                        variation.variationType === oldName
                            ? { ...variation, variationType: editName }
                            : variation
                    ),
                })),
            }));
            handleSaveEdit(index);   
        } else {
            validator.current.showMessages();
            forceUpdate(1);
        }
    };
    const checkButtonDisableHandler = () => {
        const isDuplicate = variations.some(
            variation => variation.name.normalize("NFKC").toLowerCase().trim() === newVariationName.normalize("NFKC").toLowerCase().trim()
        );
        const isNewVariationNameInvalid = newVariationName.length > 15;
        return !newVariationName.trim() || isDuplicate || isNewVariationNameInvalid;
    };
    useEffect(() => {
        const arr: number[] = [];
        variations.map((val, i) => {
            if (val?.checked) {

                arr.push(i);
            }
        });
        setaddinputbox(arr);
    }, [variations]);
    return (
        <Grid sx={{ display: "flex", justifyContent: { sm: "flex-start", md: "flex-end" } }}>
            <Card className="card" variant="outlined" sx={{ p: 2, width:"100%", borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t("product_variation_list.choose_variation_type")}
                    </Typography>
                    <List
                        values={variations.map(variation => variation.name)}
                        onChange={onDragEnd}
                        renderList={({ children, props }) => <Box {...props}>{children}</Box>}
                        renderItem={({ value, props }):any => {
                            const variation = variations.find(v => v.name === value);
                            if (!variation) return null;
                            return (
                                <Box {...props}>
                                    <Grid
                                        container
                                        spacing={2}
                                        className="variation_type_list"
                                    >
                                        <Grid item className="drag_button" xs={1} sm={1} md={2} lg={1}>
                                            <Box sx={{ mr: 1 , mt: 1, cursor: "grab" }} data-movable-handle>
                                                <DragIndicatorIcon />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={9} sm={9} md={9} lg={10}>
                                            {editIndex === variations.indexOf(variation) ? (
                                                <Grid alignItems="center">
                                                    <TextField
                                                        fullWidth
                                                        sx={{ width:{lg:140,md:140,sm:200} }}
                                                        size="small"
                                                        value={editName}
                                                        onChange={(e) => {
                                                            setEditName(e.target.value);
                                                            validator.current.showMessageFor("variationName");
                                                        }}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton size="small" color="primary" onClick={() => {
                                                                        editFieldHandler(editName.trim(), variations.indexOf(variation));
                                                                    }} disabled={editName.trim() === ""|| editName.length > 15 || variations.find(v => v.name === editName.trim()) ? true : false}>
                                                                        <CheckIcon />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                    {validator.current.message("variationName", editName, [
                                                        "required",
                                                        "maxChar15Validation:15",
                                                    ])}
                                                </Grid>
                                            ) : (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={addinputbox.includes(variations.indexOf(variation))}
                                                            onChange={() => {
                                                                // Toggle input box tracking
                                                                const isInInputBox = addinputbox.includes(variations.indexOf(variation));
                                                                if (isInInputBox) {
                                                                    setaddinputbox(addinputbox.filter(num => num !== variations.indexOf(variation)));
                                                                } else {
                                                                    setaddinputbox([...addinputbox, variations.indexOf(variation)]);
                                                                }
                                                                handleCheckboxChange(variations.indexOf(variation),variation?.name);
                                                            }
                                                            }
                                                            disabled={addinputbox.length === 2 && !addinputbox.includes(variations.indexOf(variation))}
                                                        />
                                                    }
                                                    label={variation?.name}
                                                />
                                            )}
                                            <Collapse in={addinputbox.includes(variations.indexOf(variation))}>
                                                <Grid container spacing={2} direction="column" className="radio_dropdown">
                                                    <Grid item className="radio_button">
                                                        <FormControlLabel
                                                            control={
                                                                <Radio
                                                                    checked={variation?.type === "Radio Button"}
                                                                    onChange={() => {
                                                                        handleTypeChange(variations.indexOf(variation), "Radio Button");
                                                                    }}
                                                                />
                                                            }
                                                            label={t("product_variation_list.radio_button")}
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <FormControlLabel
                                                            control={
                                                                <Radio
                                                                    checked={variation?.type === "List Drop-down"}
                                                                    onChange={() => {
                                                                        handleTypeChange(variations.indexOf(variation), "List Drop-down");
                                                                    }}
                                                                />
                                                            }
                                                            label={t("product_variation_list.list_drop_down")}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Collapse>
                                        </Grid>
                                        {variation?.isCustom && (
                                            <Grid item xs={2} sm={2} md={2} lg={0.1}>
                                                <Grid sx={{ display: "flex", justifyContent: "flex-end" }}>

                                                    {
                                                        editIndex === variations.indexOf(variation) ?
                                                            <IconButton onClick={() => {
                                                                setEditIndex(null);
                                                                setEditName(""); 
                                                                validator.current.fields = {};
                                                                forceUpdate(1);
                                                            }}>
                                                                <CloseIcon />
                                                            </IconButton>
                                                            :
                                                            <IconButton
                                                                onClick={() => { handleEditVariation(variations.indexOf(variation)); }}
                                                            >
                                                                <DriveFileRenameOutlineIcon />
                                                            </IconButton>
                                                    }
                                                    {
                                                        variation?.isCustom &&
                                                        <IconButton onClick={() => {
                                                            handleOpenModal(variations.indexOf(variation));
                                                        }}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    }
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            );
                        }}
                    />
                    {showInput ? (
                        <Grid alignItems="center">
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder={t("product_variation_list.add_new_variation") || ""}
                                value={newVariationName}
                                onChange={(e) => {
                                    setNewVariationName(e.target.value);
                                    validator.current.showMessageFor("newVariationName");
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" color="primary" onClick={addCustomVariation} disabled={checkButtonDisableHandler()}>
                                                <CheckIcon />
                                            </IconButton>
                                            <IconButton size="small" color="primary" onClick={closeCustomVariation}>
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {validator.current.message("newVariationName", newVariationName, [
                                "maxChar15Validation:15",
                            ])} 
                        </Grid>
                    ) : (
                        <Button variant="contained" color="primary" fullWidth sx={{ mt: 3 }} onClick={handleCustomVariation} disabled={editIndex !== null}>
                            {t("product_variation_list.add_custom_variation")}
                        </Button>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
};

export default VariationTypeSelect;